/**
 * Sound documents (`*.sound.json`, format `prism.sound/1`).
 *
 * TypeScript mirror of engine/docs/systems/sound.md plus the pure helpers the
 * Sound tab edits with: parse/serialize, musical time (beats/bars/seconds),
 * pitch names, keys/scales/chords, drum step strings, and immutable edits of
 * layers, tracks, clips, patterns, notes, instruments and voice lines.
 *
 * The types are deliberately open (`[key: string]: unknown`): the editor must
 * round-trip every field the engine understands, including ones this file
 * does not model.
 */

export const SOUND_FORMAT = 'prism.sound/1';
export const SAMPLE_RATE = 48000;

export type SoundKind = 'sfx' | 'instrument' | 'song' | 'voice';
export const SOUND_KINDS: readonly SoundKind[] = ['sfx', 'instrument', 'song', 'voice'];

/** A numeric parameter: number, `$expr` string, or an env/adsr/lfo/random/add/mul object. */
export type ParamValue = number | string | { [key: string]: unknown };
/** MIDI number (fractional allowed), note name `"C#4"`, `{ hz }` or scale degree `{ deg, oct }`. */
export type Pitch = number | string | { hz: number } | { deg: number; oct?: number };

export interface SoundExport {
  path?: string;
  bitDepth?: 16 | 24 | 32;
  channels?: 1 | 2;
  normalize?: number;
  trim?: boolean;
  tail?: number;
  loop?: { start: number; end: number; crossfade?: number };
  [key: string]: unknown;
}

export interface SoundEffect {
  type: string;
  mix?: ParamValue;
  bypass?: boolean;
  [key: string]: unknown;
}

export interface SoundSource {
  type: string;
  [key: string]: unknown;
}

export interface SoundLayer {
  name?: string;
  start?: number;
  gain?: ParamValue;
  pan?: ParamValue;
  mute?: boolean;
  seed?: number;
  source?: SoundSource;
  amp?: ParamValue;
  pitch?: ParamValue;
  effects?: SoundEffect[];
  send?: Record<string, number>;
  [key: string]: unknown;
}

export interface SoundInstrument {
  type: string;
  [key: string]: unknown;
}

export interface SoundNote {
  /** Start in beats from the pattern start (number or note value like `"1/16"`). */
  t: number | string;
  /** Duration in beats (number or note value like `"1/8t"`, `"1/4."`). */
  d: number | string;
  p?: Pitch | Pitch[];
  /** Chord root octave (default 4). */
  oct?: number;
  v?: number;
  chord?: string;
  arp?: { mode?: string; rate?: string; [key: string]: unknown };
  bend?: ParamValue;
  lyric?: string;
  x?: Record<string, ParamValue>;
  [key: string]: unknown;
}

export interface SoundPattern {
  /** Length in beats (number or note value). */
  length?: number | string;
  /** Length in bars (when `length` is absent). */
  bars?: number;
  /** Drum steps per bar (default 4 per beat). */
  steps?: number;
  notes?: SoundNote[];
  scale?: string;
  /** Drum lanes: GM name / MIDI key / note name → step string (`x...X...-...`), bar array or `{ steps, key, v }`. */
  drums?: Record<string, DrumLane>;
  [key: string]: unknown;
}

export type DrumLane = string | string[] | { steps: string | string[]; key?: number | string; v?: number; [key: string]: unknown };

export interface SoundClip {
  /** Bars (0 = first bar) or `"bar.beat"` (beat counted from 1). */
  start: number | string;
  pattern: string;
  repeat?: number;
  [key: string]: unknown;
}

export interface SoundTrack {
  name?: string;
  instrument?: string;
  gain?: ParamValue;
  pan?: ParamValue;
  mute?: boolean;
  effects?: SoundEffect[];
  send?: Record<string, number>;
  clips?: SoundClip[];
  automation?: Record<string, ParamValue>;
  [key: string]: unknown;
}

export interface SoundVoiceLine {
  text: string;
  start?: number;
  speed?: number;
  pitch?: number;
  energy?: number;
  noise?: number;
  pause?: number;
  phonemes?: string | null;
  emphasis?: string[];
  speaker?: number;
  [key: string]: unknown;
}

export interface SoundVoice {
  mode?: 'speech' | 'sing';
  voice?: string;
  lines?: SoundVoiceLine[];
  notes?: SoundNote[];
  effects?: SoundEffect[];
  [key: string]: unknown;
}

export interface SoundReturn {
  gain?: number;
  effects?: SoundEffect[];
  [key: string]: unknown;
}

export interface SoundMaster {
  gain?: number;
  effects?: SoundEffect[];
  returns?: Record<string, SoundReturn>;
  limiter?: boolean;
  loudness?: number;
  [key: string]: unknown;
}

export interface SoundVariant {
  name: string;
  variables?: Record<string, number>;
  seed?: number;
  [key: string]: unknown;
}

export interface SoundDoc {
  format: typeof SOUND_FORMAT;
  name: string;
  kind: SoundKind;
  duration?: number;
  export?: SoundExport;
  seed?: number;
  variables?: Record<string, ParamValue>;
  variants?: SoundVariant[];
  layers?: SoundLayer[];
  tempo?: number;
  timeSignature?: [number, number];
  key?: string;
  swing?: number;
  instruments?: Record<string, SoundInstrument>;
  /** The instrument this document defines (`kind: "instrument"`). */
  instrument?: SoundInstrument;
  tracks?: SoundTrack[];
  patterns?: Record<string, SoundPattern>;
  voice?: SoundVoice;
  master?: SoundMaster;
  [key: string]: unknown;
}

/* ------------------------------------------------------------------ */
/* Parse / serialize                                                   */

export function parseSound(text: string): SoundDoc {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (error) {
    throw new Error(`invalid JSON: ${error instanceof Error ? error.message : String(error)}`);
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('a sound document must be a JSON object');
  const doc = raw as Record<string, unknown>;
  if (doc.format !== SOUND_FORMAT) throw new Error(`"format" must be "${SOUND_FORMAT}"`);
  const kind = SOUND_KINDS.includes(doc.kind as SoundKind) ? (doc.kind as SoundKind) : 'sfx';
  return { ...(doc as object), format: SOUND_FORMAT, name: typeof doc.name === 'string' ? doc.name : '', kind } as SoundDoc;
}

function isPlain(value: unknown): boolean {
  return value === null || typeof value !== 'object';
}

/**
 * JSON with small leaf objects/arrays on one line (`[0, 1, "exp"]`,
 * `{ "t": 0, "d": 1, "p": "A3" }`) so notes and envelope points read well and
 * diff line by line.
 */
export function stringifySound(value: unknown, indent = ''): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    if (value.every(isPlain) || value.every((v) => Array.isArray(v) && v.every(isPlain))) {
      const flat = `[${value.map((v) => stringifySound(v ?? null, '')).join(', ')}]`;
      if (flat.length + indent.length <= 110) return flat;
    }
    const inner = indent + '  ';
    return `[\n${value.map((v) => inner + stringifySound(v ?? null, inner)).join(',\n')}\n${indent}]`;
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '{}';
    if (entries.every(([, v]) => isPlain(v) || (Array.isArray(v) && v.every(isPlain)))) {
      const flat = `{ ${entries.map(([k, v]) => `${JSON.stringify(k)}: ${stringifySound(v, '')}`).join(', ')} }`;
      if (flat.length + indent.length <= 100) return flat;
    }
    const inner = indent + '  ';
    return `{\n${entries.map(([k, v]) => `${inner}${JSON.stringify(k)}: ${stringifySound(v, inner)}`).join(',\n')}\n${indent}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

export function serializeSound(doc: SoundDoc): string {
  return `${stringifySound(doc)}\n`;
}

export function slugSoundName(name: string): string {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'sound';
}

/** A minimal, valid starting document for each kind. */
export function newSoundDoc(name: string, kind: SoundKind = 'sfx'): SoundDoc {
  const base: SoundDoc = {
    format: SOUND_FORMAT,
    name,
    kind,
    export: { path: `audio/${slugSoundName(name)}.wav`, bitDepth: 24, channels: 2, normalize: -1 },
    seed: 1,
  };
  switch (kind) {
    case 'song':
      return {
        ...base,
        tempo: 120,
        timeSignature: [4, 4],
        key: 'C major',
        instruments: { piano: { type: 'soundfont', bank: 0, program: 0 } },
        patterns: { main: { length: 4, notes: [] } },
        tracks: [{ name: 'Piano', instrument: 'piano', gain: -6, clips: [{ start: 0, pattern: 'main', repeat: 4 }] }],
        master: { limiter: true, loudness: -14 },
      };
    case 'instrument':
      return {
        ...base,
        instrument: {
          type: 'synth',
          voices: 8,
          source: { type: 'osc', wave: 'saw' },
          amp: { adsr: [0.01, 0.2, 0.7, 0.3] },
        },
        tempo: 120,
        patterns: { preview: { length: 4, notes: [{ t: 0, d: 1, p: 'C4', v: 0.8 }, { t: 1, d: 1, p: 'E4', v: 0.8 }, { t: 2, d: 2, p: 'G4', v: 0.8 }] } },
      };
    case 'voice':
      return {
        ...base,
        voice: { mode: 'speech', voice: 'en_US-amy-medium', lines: [{ text: 'Hello there.', start: 0 }] },
        master: { limiter: true },
      };
    default:
      return {
        ...base,
        duration: 0.6,
        layers: [
          {
            name: 'tone',
            start: 0,
            gain: -6,
            source: { type: 'osc', wave: 'sine', freq: 440 },
            amp: { env: [[0, 0], [0.005, 1], [0.5, 0, 'exp']] },
          },
        ],
        master: { limiter: true },
      };
  }
}

/* ------------------------------------------------------------------ */
/* Generic path access (inspectors)                                    */

export type DocPath = ReadonlyArray<string | number>;

export function getAtPath(root: unknown, path: DocPath): unknown {
  let cur: unknown = root;
  for (const key of path) {
    if (cur === null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string | number, unknown>)[key];
  }
  return cur;
}

/** Immutable set; `undefined` removes the key (or array element). */
export function setAtPath<T>(root: T, path: DocPath, value: unknown): T {
  if (path.length === 0) return value as T;
  const [head, ...rest] = path as [string | number, ...Array<string | number>];
  const container: unknown = root;
  if (Array.isArray(container)) {
    const copy = container.slice();
    const index = Number(head);
    const next = setAtPath(copy[index], rest, value);
    if (next === undefined && rest.length === 0) copy.splice(index, 1);
    else copy[index] = next;
    return copy as T;
  }
  const obj = container && typeof container === 'object' ? { ...(container as Record<string, unknown>) } : {};
  const child = obj[String(head)] ?? (typeof rest[0] === 'number' ? [] : undefined);
  const next = setAtPath(child, rest, value);
  if (next === undefined) delete obj[String(head)];
  else obj[String(head)] = next;
  return obj as T;
}

/* ------------------------------------------------------------------ */
/* Musical time                                                        */
/*
 * Conventions (sound.md "Songs"): `tempo` counts quarter-note beats per
 * minute; pattern `t`/`d` are those beats; a bar holds
 * `timeSignature[0] * 4 / timeSignature[1]` beats; clip `start` is in bars
 * from 0, or `"bar.beat"` with the beat counted from 1 (`"2.3"` = bar 2 +
 * 2 beats). Drum step strings have 16 steps per bar.
 */

export const STEPS_PER_BAR = 16;

export function tempoOf(doc: Pick<SoundDoc, 'tempo'>): number {
  return typeof doc.tempo === 'number' && doc.tempo > 0 ? doc.tempo : 120;
}

export function timeSignatureOf(doc: Pick<SoundDoc, 'timeSignature'>): [number, number] {
  const ts = doc.timeSignature;
  if (Array.isArray(ts) && ts[0]! > 0 && ts[1]! > 0) return [ts[0]!, ts[1]!];
  return [4, 4];
}

export function secondsPerBeat(tempo: number): number {
  return 60 / (tempo > 0 ? tempo : 120);
}

export function beatsPerBar(ts: readonly [number, number]): number {
  return (ts[0] * 4) / ts[1];
}

export function beatsToSeconds(beats: number, tempo: number): number {
  return beats * secondsPerBeat(tempo);
}

export function secondsToBeats(seconds: number, tempo: number): number {
  return seconds / secondsPerBeat(tempo);
}

export function barsToBeats(bars: number, ts: readonly [number, number]): number {
  return bars * beatsPerBar(ts);
}

export function beatsToBars(beats: number, ts: readonly [number, number]): number {
  return beats / beatsPerBar(ts);
}

/** Clip start (`2`, `2.5`, `"2.3"`) → beats from the song start. */
export function barPosToBeats(pos: number | string | undefined, ts: readonly [number, number]): number {
  if (pos === undefined || pos === null) return 0;
  if (typeof pos === 'number') return Number.isFinite(pos) ? barsToBeats(pos, ts) : 0;
  const text = String(pos).trim();
  const m = text.match(/^(-?\d+)(?:\.(\d+))?(?:\.(\d+))?$/);
  if (!m) {
    const n = Number(text);
    return Number.isFinite(n) ? barsToBeats(n, ts) : 0;
  }
  const bar = Number(m[1]);
  const beat = m[2] === undefined ? 1 : Number(m[2]);
  const sixteenth = m[3] === undefined ? 1 : Number(m[3]);
  return barsToBeats(bar, ts) + Math.max(0, beat - 1) + Math.max(0, sixteenth - 1) * 0.25;
}

/**
 * Beats → clip start value: a bar number on a bar line, `"bar.beat"` on a
 * beat, `"bar.beat.sixteenth"` on a sixteenth, else fractional bars.
 */
export function beatsToBarPos(beats: number, ts: readonly [number, number]): number | string {
  const bpb = beatsPerBar(ts);
  const clean = Math.max(0, round6(beats));
  const bar = Math.floor(clean / bpb + 1e-9);
  const within = round6(clean - bar * bpb);
  if (within < 1e-6) return bar;
  const beat = Math.floor(within + 1e-9);
  const six = round6((within - beat) * 4);
  if (Math.abs(six - Math.round(six)) < 1e-6) return Math.round(six) === 0 ? `${bar}.${beat + 1}` : `${bar}.${beat + 1}.${Math.round(six) + 1}`;
  return round6(clean / bpb);
}

/** Note value (`"1/16"` = 0.25, `"1/8t"` = 1/3, `"1/4."` = 1.5, `"1"` = 4) or number → beats. */
export function beatsValue(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  if (typeof value !== 'string') return fallback;
  let t = value.trim();
  let mul = 1;
  if (/[tT]$/.test(t)) {
    mul = 2 / 3;
    t = t.slice(0, -1);
  } else if (/[.d]$/.test(t)) {
    mul = 1.5;
    t = t.slice(0, -1);
  }
  const [a, b] = t.split('/');
  const whole = b === undefined ? Number(a) : Number(a) / Number(b);
  return Number.isFinite(whole) && whole > 0 ? whole * 4 * mul : fallback;
}

/** A note's start and duration in beats. */
export function noteStart(note: Pick<SoundNote, 't'>): number {
  return beatsValue(note.t, 0);
}

export function noteDur(note: Pick<SoundNote, 'd'>): number {
  return beatsValue(note.d, 1);
}

/** Velocity 0..1 (values > 1 are MIDI 0..127; default 0.8). */
export function noteVelocity(note: Pick<SoundNote, 'v'>): number {
  const v = note.v;
  if (typeof v !== 'number' || !Number.isFinite(v)) return 0.8;
  return v > 1 ? Math.min(1, v / 127) : Math.max(0, v);
}

/** Human position `bar.beat` (both counted from 1) for the transport display. */
export function formatBarBeat(beats: number, ts: readonly [number, number]): string {
  const bpb = beatsPerBar(ts);
  const b = Math.max(0, beats);
  const bar = Math.floor(b / bpb + 1e-9);
  const beat = b - bar * bpb;
  return `${bar + 1}.${Math.floor(beat + 1e-9) + 1}`;
}

export function formatSeconds(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  return `${m}:${rest.toFixed(2).padStart(5, '0')}`;
}

function round6(n: number): number {
  return Math.round(n * 1e6) / 1e6;
}

export function snapTo(value: number, grid: number): number {
  if (!(grid > 0)) return value;
  return round6(Math.round(value / grid) * grid);
}

/* ------------------------------------------------------------------ */
/* Pitch                                                               */

const NOTE_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/** Pitch class of a note name like `C#`, `Bb` (null when unknown). */
export function pitchClass(name: string): number | null {
  const m = name.trim().match(/^([A-Ga-g])([#b♯♭]*)$/);
  if (!m) return null;
  let pc = NOTE_PC[m[1]!.toUpperCase()]!;
  for (const ch of m[2]!) pc += ch === '#' || ch === '♯' ? 1 : -1;
  return ((pc % 12) + 12) % 12;
}

/** `"C#4"` → 61 (C4 = 60). Null when it is not a note name. */
export function noteNameToMidi(name: string): number | null {
  const m = name.trim().match(/^([A-Ga-g][#b♯♭]*)(-?\d+)$/);
  if (!m) return null;
  const pc = pitchClass(m[1]!);
  if (pc === null) return null;
  // Use the written letter's octave (Cb4 = 59, B#3 = 60).
  const letter = NOTE_PC[m[1]![0]!.toUpperCase()]!;
  const accidental = pc - letter;
  const shift = accidental > 6 ? accidental - 12 : accidental < -6 ? accidental + 12 : accidental;
  return (Number(m[2]) + 1) * 12 + letter + shift;
}

export function midiToNoteName(midi: number): string {
  const n = Math.round(midi);
  return `${SHARP_NAMES[((n % 12) + 12) % 12]}${Math.floor(n / 12) - 1}`;
}

export function midiToHz(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export function hzToMidi(hz: number): number {
  return 69 + 12 * Math.log2(hz / 440);
}

/* ------------------------------------------------------------------ */
/* Keys, scales, chords                                                */

export const SCALES: Record<string, number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  ionian: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  aeolian: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  locrian: [0, 1, 3, 5, 6, 8, 10],
  'harmonic minor': [0, 2, 3, 5, 7, 8, 11],
  'melodic minor': [0, 2, 3, 5, 7, 9, 11],
  pentatonic: [0, 2, 4, 7, 9],
  'major pentatonic': [0, 2, 4, 7, 9],
  'minor pentatonic': [0, 3, 5, 7, 10],
  blues: [0, 3, 5, 6, 7, 10],
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
};

export interface KeyInfo {
  root: number;
  mode: string;
  intervals: number[];
}

/** `"A minor"`, `"F# dorian"`, `"Bb"`, `"Am"` → root pitch class + scale. */
export function parseKey(key: string | undefined | null): KeyInfo | null {
  if (!key || typeof key !== 'string') return null;
  const m = key.trim().match(/^([A-Ga-g][#b♯♭]?)\s*(.*)$/);
  if (!m) return null;
  const root = pitchClass(m[1]!);
  if (root === null) return null;
  let mode = m[2]!.trim().toLowerCase();
  if (mode === '' || mode === 'maj' || mode === 'M') mode = 'major';
  if (mode === 'm' || mode === 'min') mode = 'minor';
  const intervals = SCALES[mode];
  if (!intervals) return null;
  return { root, mode, intervals };
}

export function inScale(midi: number, key: KeyInfo | null): boolean {
  if (!key) return true;
  const pc = (((Math.round(midi) - key.root) % 12) + 12) % 12;
  return key.intervals.includes(pc);
}

/** Scale degree (1-based, may exceed the scale length) in octave `oct` → MIDI. */
export function degreeToMidi(deg: number, oct: number, key: KeyInfo): number {
  const n = key.intervals.length;
  const d = Math.round(deg) - 1;
  const octaves = Math.floor(d / n);
  const idx = ((d % n) + n) % n;
  return (oct + 1) * 12 + key.root + key.intervals[idx]! + 12 * octaves;
}

export const CHORD_QUALITIES: Record<string, number[]> = {
  '': [0, 4, 7],
  maj: [0, 4, 7],
  M: [0, 4, 7],
  m: [0, 3, 7],
  min: [0, 3, 7],
  '-': [0, 3, 7],
  '5': [0, 7],
  '6': [0, 4, 7, 9],
  m6: [0, 3, 7, 9],
  '7': [0, 4, 7, 10],
  maj7: [0, 4, 7, 11],
  M7: [0, 4, 7, 11],
  m7: [0, 3, 7, 10],
  min7: [0, 3, 7, 10],
  mMaj7: [0, 3, 7, 11],
  dim: [0, 3, 6],
  dim7: [0, 3, 6, 9],
  m7b5: [0, 3, 6, 10],
  aug: [0, 4, 8],
  '+': [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  sus: [0, 5, 7],
  '7sus4': [0, 5, 7, 10],
  add9: [0, 4, 7, 14],
  '9': [0, 4, 7, 10, 14],
  maj9: [0, 4, 7, 11, 14],
  m9: [0, 3, 7, 10, 14],
  '7b9': [0, 4, 7, 10, 13],
  '7#9': [0, 4, 7, 10, 15],
  '11': [0, 4, 7, 10, 14, 17],
  m11: [0, 3, 7, 10, 14, 17],
  '13': [0, 4, 7, 10, 14, 21],
};

/** Chord types offered by the piano-roll chord stamp. */
export const STAMP_CHORDS = ['', 'm', '7', 'maj7', 'm7', 'dim', 'aug', 'sus2', 'sus4', '6', 'add9', '9'];

/**
 * `"Am"`, `"Cmaj7/E"`, `"G7b9"` → MIDI notes with the root in `octave`
 * (default 4). A slash bass goes below the root. Null when unparseable.
 */
export function chordNotes(chord: string, octave = 4): number[] | null {
  const m = chord.trim().match(/^([A-G][#b]?)([^/]*)(?:\/([A-G][#b]?))?$/);
  if (!m) return null;
  const root = pitchClass(m[1]!);
  const quality = CHORD_QUALITIES[m[2]!];
  if (root === null || !quality) return null;
  const base = (octave + 1) * 12 + root;
  const notes = quality.map((i) => base + i);
  if (m[3]) {
    const bass = pitchClass(m[3]);
    if (bass !== null) {
      let b = (octave + 1) * 12 + bass;
      while (b >= base) b -= 12;
      notes.unshift(b);
    }
  }
  return notes;
}

/** Chord built from intervals of a quality on a MIDI root (stamp tool). */
export function stampChord(rootMidi: number, quality: string): number[] {
  return (CHORD_QUALITIES[quality] ?? CHORD_QUALITIES['']!).map((i) => rootMidi + i);
}

/** Resolve any `Pitch` to a MIDI number (null when it cannot be resolved here). */
export function pitchToMidi(pitch: Pitch | Pitch[] | undefined, key: KeyInfo | null = null): number | null {
  if (pitch === undefined || pitch === null) return null;
  if (Array.isArray(pitch)) return pitch.length ? pitchToMidi(pitch[0], key) : null;
  if (typeof pitch === 'number') return Number.isFinite(pitch) ? pitch : null;
  if (typeof pitch === 'string') return noteNameToMidi(pitch) ?? gmDrumKey(pitch);
  if (typeof pitch === 'object') {
    if ('hz' in pitch && typeof pitch.hz === 'number' && pitch.hz > 0) return hzToMidi(pitch.hz);
    if ('deg' in pitch && typeof pitch.deg === 'number' && key) {
      const acc = (pitch as { acc?: unknown }).acc;
      return degreeToMidi(pitch.deg, pitch.oct ?? 4, key) + (typeof acc === 'number' ? acc : 0);
    }
  }
  return null;
}

/** The MIDI pitches a note sounds (chords expanded) — for display. */
export function notePitches(note: SoundNote, key: KeyInfo | null = null): number[] {
  if (note.chord) return chordNotes(note.chord, typeof note.oct === 'number' ? note.oct : 4) ?? [];
  if (Array.isArray(note.p)) return note.p.map((p) => pitchToMidi(p, key)).filter((p): p is number => p !== null);
  const p = pitchToMidi(note.p, key);
  return p === null ? [] : [p];
}

/** Write a MIDI pitch back in the style of the original (`"C4"` stays a name). */
export function withPitchStyle(original: Pitch | Pitch[] | undefined, midi: number): Pitch {
  if (typeof original === 'string' || original === undefined) return Number.isInteger(midi) ? midiToNoteName(midi) : midi;
  return midi;
}

/* ------------------------------------------------------------------ */
/* Drum step strings                                                   */

export type StepValue = 'x' | 'X' | '-' | '.' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';

/** General MIDI drum lanes (names as prism_sound's `gm_drum_key` reads them). */
export const GM_DRUMS: Record<string, number> = {
  kick: 36,
  kick2: 35,
  rim: 37,
  snare: 38,
  clap: 39,
  snare2: 40,
  floorTom: 41,
  closedHat: 42,
  pedalHat: 44,
  lowTom: 45,
  openHat: 46,
  tom: 47,
  highTom: 50,
  crash: 49,
  ride: 51,
  china: 52,
  rideBell: 53,
  tambourine: 54,
  splash: 55,
  cowbell: 56,
  bongo: 60,
  conga: 63,
  maracas: 70,
  claves: 75,
  woodblock: 76,
  triangle: 81,
  shaker: 82,
};

const DRUM_ALIASES: Record<string, number> = { hat: 42, hh: 42, hihat: 42, bd: 36, sd: 38, oh: 46, ohh: 46, cp: 39 };

/** MIDI key of a GM drum name (`"kick"`, `"closed_hat"`), null when unknown. */
export function gmDrumKey(name: string): number | null {
  const k = name.replace(/[\s_-]/g, '').toLowerCase();
  for (const [n, v] of Object.entries(GM_DRUMS)) if (n.toLowerCase() === k) return v;
  return DRUM_ALIASES[k] ?? null;
}

/** Steps per bar for drum strings (`pattern.steps`, default 4 per beat). */
export function stepsPerBar(pattern: Pick<SoundPattern, 'steps'> | undefined, ts: readonly [number, number]): number {
  const n = pattern?.steps;
  return typeof n === 'number' && n >= 1 ? Math.round(n) : Math.max(1, Math.round(beatsPerBar(ts) * 4));
}

/** The step string of a drum lane (string, bar array or `{ steps }`). */
export function laneSteps(lane: DrumLane | undefined): string {
  if (typeof lane === 'string') return lane;
  if (Array.isArray(lane)) return lane.join('|');
  if (lane && typeof lane === 'object') return Array.isArray(lane.steps) ? lane.steps.join('|') : String(lane.steps ?? '');
  return '';
}

/** Write a step string back into a lane, keeping an object lane's other keys. */
export function withLaneSteps(lane: DrumLane | undefined, steps: string): DrumLane {
  if (lane && typeof lane === 'object' && !Array.isArray(lane)) return { ...lane, steps };
  return steps;
}

function stepChar(c: string): StepValue {
  if (c === 'x' || c === 'o') return 'x';
  if (c === 'X' || c === 'O') return 'X';
  if (c === '-' || c === 'g') return '-';
  if (c >= '1' && c <= '9') return c as StepValue;
  return '.';
}

/**
 * Step string → one value per step. With `|` bar separators each bar is
 * padded to `perBar` steps (a short bar ends in rests), like prism_sound.
 */
export function parseSteps(text: string, perBar = STEPS_PER_BAR): StepValue[] {
  if (!text.includes('|')) return [...text.replace(/\s/g, '')].map(stepChar);
  let bars = text.split('|');
  bars = bars.filter((b, i) => !(b.trim() === '' && (i === 0 || i === bars.length - 1)));
  const out: StepValue[] = [];
  for (const bar of bars) {
    const chars = [...bar.replace(/\s/g, '')].map(stepChar);
    while (chars.length < perBar) chars.push('.');
    out.push(...chars);
  }
  return out;
}

/** Velocity of a step (0 = rest): x 0.8, X 1, - 0.4, 1-9 = n/9. */
export function stepVelocity(v: StepValue): number {
  if (v === 'x') return 0.8;
  if (v === 'X') return 1;
  if (v === '-') return 0.4;
  if (v === '.') return 0;
  return Number(v) / 9;
}

export function serializeSteps(steps: readonly StepValue[]): string {
  return steps.join('');
}

/** Pad/truncate a step string to `count` steps (bar separators are flattened). */
export function resizeSteps(text: string, count: number, perBar = STEPS_PER_BAR): string {
  const steps = parseSteps(text, perBar).slice(0, count);
  while (steps.length < count) steps.push('.');
  return serializeSteps(steps);
}

export function setStep(text: string, index: number, value: StepValue, count?: number, perBar = STEPS_PER_BAR): string {
  const steps = parseSteps(count === undefined ? text : resizeSteps(text, Math.max(count, index + 1), perBar), perBar);
  while (steps.length <= index) steps.push('.');
  steps[index] = value;
  return serializeSteps(steps);
}

/** Steps a pattern's drum lanes span (pattern length in bars × steps per bar). */
export function patternStepCount(pattern: SoundPattern, ts: readonly [number, number]): number {
  const per = stepsPerBar(pattern, ts);
  const beats = patternLength(pattern, ts);
  const fromLength = Math.max(1, Math.round((beats / beatsPerBar(ts)) * per));
  const longest = Math.max(0, ...Object.values(pattern.drums ?? {}).map((l) => parseSteps(laneSteps(l), per).length));
  return Math.max(fromLength, longest);
}

/* ------------------------------------------------------------------ */
/* Lengths and extents                                                 */

/** Pattern length in beats: `length`, else notes/drums rounded up to a bar. */
export function patternLength(pattern: SoundPattern | undefined, ts: readonly [number, number] = [4, 4]): number {
  if (!pattern) return beatsPerBar(ts);
  const bpb = beatsPerBar(ts);
  const explicit = beatsValue(pattern.length, 0);
  if (explicit > 0) return explicit;
  if (typeof pattern.bars === 'number' && pattern.bars > 0) return pattern.bars * bpb;
  const per = stepsPerBar(pattern, ts);
  let end = 0;
  for (const n of pattern.notes ?? []) end = Math.max(end, noteStart(n) + noteDur(n));
  for (const l of Object.values(pattern.drums ?? {})) end = Math.max(end, Math.ceil(parseSteps(laneSteps(l), per).length / per - 1e-9) * bpb);
  return Math.max(bpb, Math.ceil(end / bpb - 1e-9) * bpb);
}

export interface ClipSpan {
  start: number;
  end: number;
  length: number;
}

/** A clip's span in beats. */
export function clipSpan(doc: SoundDoc, clip: SoundClip): ClipSpan {
  const ts = timeSignatureOf(doc);
  const start = barPosToBeats(clip.start, ts);
  const length = patternLength(doc.patterns?.[clip.pattern], ts) * Math.max(1, clip.repeat ?? 1);
  return { start, end: start + length, length };
}

/** Song length in beats (end of the last clip). */
export function songLengthBeats(doc: SoundDoc): number {
  let end = 0;
  for (const track of doc.tracks ?? []) for (const clip of track.clips ?? []) end = Math.max(end, clipSpan(doc, clip).end);
  return end;
}

function lastEnvTime(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (Array.isArray(v.env)) {
    const last = v.env.at(-1);
    return Array.isArray(last) && typeof last[0] === 'number' ? last[0] : null;
  }
  if (Array.isArray(v.adsr)) {
    const [a, d, , r] = v.adsr as number[];
    return (a ?? 0) + (d ?? 0) + (r ?? 0);
  }
  return null;
}

/** Best estimate of a layer's audible length in seconds (for the timeline). */
export function layerLength(layer: SoundLayer, doc: SoundDoc): number {
  const fromAmp = lastEnvTime(layer.amp);
  if (fromAmp !== null && fromAmp > 0) return fromAmp;
  const src = layer.source;
  if (src) {
    if (typeof src.duration === 'number' && src.duration > 0) return src.duration;
    if (src.type === 'sample' && typeof src.end === 'number') return Math.max(0.05, src.end - (Number(src.start) || 0));
  }
  const rest = (doc.duration ?? 1) - (layer.start ?? 0);
  return rest > 0.05 ? rest : 0.5;
}

/** Rough spoken length of a voice line in seconds (≈ 14 characters per second). */
export function voiceLineLength(line: SoundVoiceLine): number {
  const speed = typeof line.speed === 'number' && line.speed > 0 ? line.speed : 1;
  return Math.max(0.3, (line.text?.length ?? 0) / 14) * speed;
}

/** Start time of each voice line (lines without `start` follow the previous one). */
export function voiceLineStarts(voice: SoundVoice | undefined): number[] {
  let t = 0;
  return (voice?.lines ?? []).map((line) => {
    const start = typeof line.start === 'number' ? line.start : t;
    t = start + voiceLineLength(line) + (line.pause ?? 0.3);
    return start;
  });
}

/** Document length estimate in seconds (before a render tells the truth). */
export function docLengthSeconds(doc: SoundDoc): number {
  if (typeof doc.duration === 'number' && doc.duration > 0) return doc.duration;
  let end = 0;
  for (const layer of doc.layers ?? []) end = Math.max(end, (layer.start ?? 0) + layerLength(layer, doc));
  if (doc.tracks?.length) end = Math.max(end, beatsToSeconds(songLengthBeats(doc), tempoOf(doc)));
  const starts = voiceLineStarts(doc.voice);
  (doc.voice?.lines ?? []).forEach((line, i) => (end = Math.max(end, starts[i]! + voiceLineLength(line))));
  if (doc.kind === 'instrument' && doc.patterns) {
    const ts = timeSignatureOf(doc);
    const longest = Math.max(0, ...Object.values(doc.patterns).map((p) => patternLength(p, ts)));
    end = Math.max(end, beatsToSeconds(longest, tempoOf(doc)));
  }
  return end > 0 ? end : 1;
}

/* ------------------------------------------------------------------ */
/* Names                                                               */

export function uniqueName(base: string, taken: Iterable<string>): string {
  const set = new Set(taken);
  if (!set.has(base)) return base;
  const stem = base.replace(/_\d+$/, '');
  for (let i = 2; ; i++) if (!set.has(`${stem}_${i}`)) return `${stem}_${i}`;
}

/* ------------------------------------------------------------------ */
/* Edits (all immutable: they return a new document)                   */

function arr<T>(list: readonly T[] | undefined): T[] {
  return list ? list.slice() : [];
}

function moveIndex<T>(list: readonly T[], index: number, delta: number): { list: T[]; index: number } {
  const to = Math.max(0, Math.min(list.length - 1, index + delta));
  const copy = list.slice();
  if (to === index || index < 0 || index >= list.length) return { list: copy, index };
  const [item] = copy.splice(index, 1);
  copy.splice(to, 0, item!);
  return { list: copy, index: to };
}

// Layers

export function addLayer(doc: SoundDoc, layer: SoundLayer, at?: number): { doc: SoundDoc; index: number } {
  const layers = arr(doc.layers);
  const index = at === undefined ? layers.length : Math.max(0, Math.min(layers.length, at));
  const name = uniqueName(layer.name || 'layer', layers.map((l) => l.name ?? ''));
  layers.splice(index, 0, { ...layer, name });
  return { doc: { ...doc, layers }, index };
}

export function updateLayer(doc: SoundDoc, index: number, patch: Partial<SoundLayer>): SoundDoc {
  const layers = arr(doc.layers);
  if (!layers[index]) return doc;
  layers[index] = dropUndefined({ ...layers[index], ...patch });
  return { ...doc, layers };
}

export function deleteLayer(doc: SoundDoc, index: number): SoundDoc {
  const layers = arr(doc.layers);
  if (!layers[index]) return doc;
  layers.splice(index, 1);
  return { ...doc, layers };
}

export function duplicateLayer(doc: SoundDoc, index: number): { doc: SoundDoc; index: number } {
  const layer = doc.layers?.[index];
  if (!layer) return { doc, index };
  return addLayer(doc, structuredClone(layer), index + 1);
}

export function moveLayer(doc: SoundDoc, index: number, delta: number): { doc: SoundDoc; index: number } {
  const result = moveIndex(arr(doc.layers), index, delta);
  return { doc: { ...doc, layers: result.list }, index: result.index };
}

/** Shift a layer in time (seconds, clamped at 0). */
export function nudgeLayer(doc: SoundDoc, index: number, seconds: number): SoundDoc {
  const layer = doc.layers?.[index];
  if (!layer) return doc;
  return updateLayer(doc, index, { start: round6(Math.max(0, (layer.start ?? 0) + seconds)) });
}

/** Scale the times of an `{ env }` value so it lasts `length` seconds (timeline resize). */
export function scaleEnvelope(value: ParamValue | undefined, length: number): ParamValue | undefined {
  if (!value || typeof value !== 'object' || !Array.isArray((value as { env?: unknown }).env)) return value;
  const env = (value as { env: unknown[] }).env;
  const last = lastEnvTime(value);
  if (!last || last <= 0 || !(length > 0)) return value;
  const k = length / last;
  return {
    ...(value as object),
    env: env.map((p) => (Array.isArray(p) && typeof p[0] === 'number' ? [round6(p[0] * k), ...p.slice(1)] : p)),
  };
}

// Tracks

export function addTrack(doc: SoundDoc, track: SoundTrack, at?: number): { doc: SoundDoc; index: number } {
  const tracks = arr(doc.tracks);
  const index = at === undefined ? tracks.length : Math.max(0, Math.min(tracks.length, at));
  const name = uniqueName(track.name || 'Track', tracks.map((t) => t.name ?? ''));
  tracks.splice(index, 0, { clips: [], ...track, name });
  return { doc: { ...doc, tracks }, index };
}

export function updateTrack(doc: SoundDoc, index: number, patch: Partial<SoundTrack>): SoundDoc {
  const tracks = arr(doc.tracks);
  if (!tracks[index]) return doc;
  tracks[index] = dropUndefined({ ...tracks[index], ...patch });
  return { ...doc, tracks };
}

export function deleteTrack(doc: SoundDoc, index: number): SoundDoc {
  const tracks = arr(doc.tracks);
  if (!tracks[index]) return doc;
  tracks.splice(index, 1);
  return { ...doc, tracks };
}

export function duplicateTrack(doc: SoundDoc, index: number): { doc: SoundDoc; index: number } {
  const track = doc.tracks?.[index];
  if (!track) return { doc, index };
  return addTrack(doc, structuredClone(track), index + 1);
}

export function moveTrack(doc: SoundDoc, index: number, delta: number): { doc: SoundDoc; index: number } {
  const result = moveIndex(arr(doc.tracks), index, delta);
  return { doc: { ...doc, tracks: result.list }, index: result.index };
}

// Clips

function withClips(doc: SoundDoc, track: number, edit: (clips: SoundClip[]) => SoundClip[]): SoundDoc {
  const t = doc.tracks?.[track];
  if (!t) return doc;
  return updateTrack(doc, track, { clips: edit(arr(t.clips)) });
}

export function addClip(doc: SoundDoc, track: number, clip: SoundClip): { doc: SoundDoc; index: number } {
  const t = doc.tracks?.[track];
  if (!t) return { doc, index: -1 };
  const index = t.clips?.length ?? 0;
  return { doc: withClips(doc, track, (clips) => [...clips, clip]), index };
}

export function updateClip(doc: SoundDoc, track: number, index: number, patch: Partial<SoundClip>): SoundDoc {
  return withClips(doc, track, (clips) => {
    if (clips[index]) clips[index] = dropUndefined({ ...clips[index], ...patch });
    return clips;
  });
}

export function deleteClip(doc: SoundDoc, track: number, index: number): SoundDoc {
  return withClips(doc, track, (clips) => clips.filter((_, i) => i !== index));
}

/** Move a clip to a start in beats (stored as bars / "bar.beat"), optionally to another track. */
export function moveClip(
  doc: SoundDoc,
  track: number,
  index: number,
  startBeats: number,
  toTrack = track,
): { doc: SoundDoc; track: number; index: number } {
  const clip = doc.tracks?.[track]?.clips?.[index];
  if (!clip || !doc.tracks?.[toTrack]) return { doc, track, index };
  const start = beatsToBarPos(Math.max(0, startBeats), timeSignatureOf(doc));
  if (toTrack === track) return { doc: updateClip(doc, track, index, { start }), track, index };
  const removed = deleteClip(doc, track, index);
  const added = addClip(removed, toTrack, { ...clip, start });
  return { doc: added.doc, track: toTrack, index: added.index };
}

/** Set a clip's length in pattern repetitions (≥ 1). */
export function resizeClip(doc: SoundDoc, track: number, index: number, repeat: number): SoundDoc {
  const r = Math.max(1, Math.round(repeat));
  return updateClip(doc, track, index, { repeat: r === 1 ? undefined : r });
}

/** Copy a clip right after itself. */
export function duplicateClip(doc: SoundDoc, track: number, index: number): { doc: SoundDoc; index: number } {
  const clip = doc.tracks?.[track]?.clips?.[index];
  if (!clip) return { doc, index };
  const span = clipSpan(doc, clip);
  const copy = { ...structuredClone(clip), start: beatsToBarPos(span.end, timeSignatureOf(doc)) };
  return addClip(doc, track, copy);
}

// Patterns and notes

export function addPattern(doc: SoundDoc, base = 'pattern', pattern?: SoundPattern): { doc: SoundDoc; name: string } {
  const patterns = { ...(doc.patterns ?? {}) };
  const name = uniqueName(base, Object.keys(patterns));
  patterns[name] = pattern ?? { length: beatsPerBar(timeSignatureOf(doc)), notes: [] };
  return { doc: { ...doc, patterns }, name };
}

/** Rename a pattern and every clip that plays it. */
export function renamePattern(doc: SoundDoc, from: string, to: string): SoundDoc {
  const patterns = doc.patterns ?? {};
  if (!patterns[from] || !to || from === to || patterns[to]) return doc;
  const next: Record<string, SoundPattern> = {};
  for (const [k, v] of Object.entries(patterns)) next[k === from ? to : k] = v;
  return {
    ...doc,
    patterns: next,
    tracks: doc.tracks?.map((t) => ({ ...t, clips: t.clips?.map((c) => (c.pattern === from ? { ...c, pattern: to } : c)) })),
  };
}

/** Delete a pattern and the clips that play it. */
export function deletePattern(doc: SoundDoc, name: string): SoundDoc {
  if (!doc.patterns?.[name]) return doc;
  const patterns = { ...doc.patterns };
  delete patterns[name];
  return {
    ...doc,
    patterns,
    tracks: doc.tracks?.map((t) => ({ ...t, clips: t.clips?.filter((c) => c.pattern !== name) })),
  };
}

export function duplicatePattern(doc: SoundDoc, name: string): { doc: SoundDoc; name: string } {
  const p = doc.patterns?.[name];
  if (!p) return { doc, name };
  return addPattern(doc, name, structuredClone(p));
}

export function updatePattern(doc: SoundDoc, name: string, patch: Partial<SoundPattern>): SoundDoc {
  const p = doc.patterns?.[name];
  if (!p) return doc;
  return { ...doc, patterns: { ...doc.patterns, [name]: dropUndefined({ ...p, ...patch }) } };
}

function withNotes(doc: SoundDoc, pattern: string, edit: (notes: SoundNote[]) => SoundNote[]): SoundDoc {
  const p = doc.patterns?.[pattern];
  if (!p) return doc;
  return updatePattern(doc, pattern, { notes: edit(arr(p.notes)) });
}

/** Notes sorted by time then pitch keep diffs stable. */
function sortNotes(notes: SoundNote[]): SoundNote[] {
  return notes
    .map((n, i) => ({ n, i }))
    .sort((a, b) => noteStart(a.n) - noteStart(b.n) || (pitchToMidi(a.n.p) ?? 0) - (pitchToMidi(b.n.p) ?? 0) || a.i - b.i)
    .map((x) => x.n);
}

/** Add notes; returns the indices of the new notes in the (sorted) list. */
export function addNotes(doc: SoundDoc, pattern: string, added: readonly SoundNote[]): { doc: SoundDoc; indices: number[] } {
  const p = doc.patterns?.[pattern];
  if (!p) return { doc, indices: [] };
  const fresh = added.map((n) => ({ ...n, t: round6(Math.max(0, noteStart(n))), d: round6(Math.max(1 / 64, noteDur(n))) }));
  const sorted = sortNotes([...(p.notes ?? []), ...fresh]);
  const next = updatePattern(doc, pattern, { notes: sorted });
  return { doc: next, indices: fresh.map((n) => sorted.indexOf(n)) };
}

export function updateNote(doc: SoundDoc, pattern: string, index: number, patch: Partial<SoundNote>): SoundDoc {
  return withNotes(doc, pattern, (notes) => {
    if (notes[index]) notes[index] = dropUndefined({ ...notes[index], ...patch }) as SoundNote;
    return notes;
  });
}

export function deleteNotes(doc: SoundDoc, pattern: string, indices: readonly number[]): SoundDoc {
  const drop = new Set(indices);
  return withNotes(doc, pattern, (notes) => notes.filter((_, i) => !drop.has(i)));
}

/**
 * Move notes by `dt` beats and `dp` semitones (time clamped at 0). Returns
 * the new indices of the moved notes after re-sorting.
 */
export function moveNotes(
  doc: SoundDoc,
  pattern: string,
  indices: readonly number[],
  dt: number,
  dp: number,
): { doc: SoundDoc; indices: number[] } {
  const p = doc.patterns?.[pattern];
  if (!p?.notes) return { doc, indices: [...indices] };
  const pick = new Set(indices);
  const moved: SoundNote[] = [];
  const notes = p.notes.map((n, i) => {
    if (!pick.has(i)) return n;
    const next: SoundNote = dt ? { ...n, t: round6(Math.max(0, noteStart(n) + dt)) } : { ...n };
    if (dp && !n.chord) {
      // Numbers and note names move; drum names, degrees and pitch lists stay.
      const movable = typeof n.p === 'number' || (typeof n.p === 'string' && noteNameToMidi(n.p) !== null);
      const midi = movable ? pitchToMidi(n.p) : null;
      if (midi !== null) next.p = withPitchStyle(n.p, midi + dp);
    } else if (dp && n.chord) {
      next.chord = transposeChord(n.chord, dp);
    }
    moved.push(next);
    return next;
  });
  const sorted = sortNotes(notes);
  return { doc: updatePattern(doc, pattern, { notes: sorted }), indices: moved.map((n) => sorted.indexOf(n)) };
}

/** Change note durations by `dd` beats (minimum `min`). */
export function resizeNotes(doc: SoundDoc, pattern: string, indices: readonly number[], dd: number, min = 1 / 32): SoundDoc {
  const pick = new Set(indices);
  return withNotes(doc, pattern, (notes) => notes.map((n, i) => (pick.has(i) ? { ...n, d: round6(Math.max(min, noteDur(n) + dd)) } : n)));
}

export function setNotesVelocity(doc: SoundDoc, pattern: string, indices: readonly number[], v: number): SoundDoc {
  const pick = new Set(indices);
  const vel = Math.round(Math.max(0, Math.min(1, v)) * 1000) / 1000;
  return withNotes(doc, pattern, (notes) => notes.map((n, i) => (pick.has(i) ? { ...n, v: vel } : n)));
}

/** Copy notes `offset` beats later (default: right after the selection). */
export function duplicateNotes(
  doc: SoundDoc,
  pattern: string,
  indices: readonly number[],
  offset?: number,
): { doc: SoundDoc; indices: number[] } {
  const notes = doc.patterns?.[pattern]?.notes ?? [];
  const picked = indices.map((i) => notes[i]).filter((n): n is SoundNote => !!n);
  if (!picked.length) return { doc, indices: [] };
  const start = Math.min(...picked.map(noteStart));
  const end = Math.max(...picked.map((n) => noteStart(n) + noteDur(n)));
  const dt = offset ?? end - start;
  return addNotes(doc, pattern, picked.map((n) => ({ ...structuredClone(n), t: noteStart(n) + dt })));
}

/** Snap note starts (and optionally lengths) to a grid in beats. */
export function quantizeNotes(doc: SoundDoc, pattern: string, indices: readonly number[], grid: number, lengths = false): SoundDoc {
  const pick = new Set(indices);
  return withNotes(doc, pattern, (notes) =>
    sortNotes(notes.map((n, i) => (pick.has(i) ? { ...n, t: snapTo(noteStart(n), grid), ...(lengths ? { d: Math.max(grid, snapTo(noteDur(n), grid)) } : {}) } : n))),
  );
}

export function transposeChord(chord: string, semitones: number): string {
  const m = chord.match(/^([A-G][#b]?)([^/]*)(?:\/([A-G][#b]?))?$/);
  if (!m) return chord;
  const shift = (name: string) => SHARP_NAMES[((((pitchClass(name) ?? 0) + semitones) % 12) + 12) % 12]!;
  return `${shift(m[1]!)}${m[2]}${m[3] ? `/${shift(m[3])}` : ''}`;
}

// Drum rows

/** Set a lane's step string (keeps `{ steps, key, v }` lanes' other keys); `undefined` removes the lane. */
export function setDrumRow(doc: SoundDoc, pattern: string, row: string, steps: string | undefined): SoundDoc {
  const p = doc.patterns?.[pattern];
  if (!p) return doc;
  const drums = { ...(p.drums ?? {}) };
  if (steps === undefined) delete drums[row];
  else drums[row] = withLaneSteps(drums[row], steps);
  return updatePattern(doc, pattern, { drums: Object.keys(drums).length ? drums : undefined });
}

export function toggleDrumStep(doc: SoundDoc, pattern: string, row: string, index: number, value: StepValue = 'x'): SoundDoc {
  const p = doc.patterns?.[pattern];
  if (!p) return doc;
  const ts = timeSignatureOf(doc);
  const per = stepsPerBar(p, ts);
  const count = patternStepCount(p, ts);
  const current = resizeSteps(laneSteps(p.drums?.[row]), count, per);
  const now = parseSteps(current, per)[index];
  return setDrumRow(doc, pattern, row, setStep(current, index, now === value ? '.' : value, count, per));
}

// Instruments

export function addInstrument(doc: SoundDoc, base: string, def: SoundInstrument): { doc: SoundDoc; name: string } {
  const instruments = { ...(doc.instruments ?? {}) };
  const name = uniqueName(base, Object.keys(instruments));
  instruments[name] = def;
  return { doc: { ...doc, instruments }, name };
}

export function renameInstrument(doc: SoundDoc, from: string, to: string): SoundDoc {
  const all = doc.instruments ?? {};
  if (!all[from] || !to || from === to || all[to]) return doc;
  const next: Record<string, SoundInstrument> = {};
  for (const [k, v] of Object.entries(all)) next[k === from ? to : k] = v;
  return {
    ...doc,
    instruments: next,
    tracks: doc.tracks?.map((t) => (t.instrument === from ? { ...t, instrument: to } : t)),
  };
}

export function deleteInstrument(doc: SoundDoc, name: string): SoundDoc {
  if (!doc.instruments?.[name]) return doc;
  const instruments = { ...doc.instruments };
  delete instruments[name];
  return { ...doc, instruments };
}

// Voice lines

export function addVoiceLine(doc: SoundDoc, line: SoundVoiceLine, at?: number): { doc: SoundDoc; index: number } {
  const voice: SoundVoice = doc.voice ?? { mode: 'speech', voice: 'en_US-amy-medium', lines: [] };
  const lines = arr(voice.lines);
  const index = at === undefined ? lines.length : Math.max(0, Math.min(lines.length, at));
  lines.splice(index, 0, line);
  return { doc: { ...doc, voice: { ...voice, lines } }, index };
}

export function updateVoiceLine(doc: SoundDoc, index: number, patch: Partial<SoundVoiceLine>): SoundDoc {
  const lines = arr(doc.voice?.lines);
  if (!lines[index]) return doc;
  lines[index] = dropUndefined({ ...lines[index], ...patch }) as SoundVoiceLine;
  return { ...doc, voice: { ...doc.voice, lines } };
}

export function deleteVoiceLine(doc: SoundDoc, index: number): SoundDoc {
  const lines = arr(doc.voice?.lines);
  if (!lines[index]) return doc;
  lines.splice(index, 1);
  return { ...doc, voice: { ...doc.voice, lines } };
}

export function duplicateVoiceLine(doc: SoundDoc, index: number): { doc: SoundDoc; index: number } {
  const line = doc.voice?.lines?.[index];
  if (!line) return { doc, index };
  const copy = structuredClone(line);
  if (typeof copy.start === 'number') copy.start = round6(copy.start + voiceLineLength(line) + (line.pause ?? 0.3));
  return addVoiceLine(doc, copy, index + 1);
}

// Kind switch

/** Change the kind, adding the section that kind needs when it is missing. */
export function setKind(doc: SoundDoc, kind: SoundKind): SoundDoc {
  if (doc.kind === kind) return doc;
  const fresh = newSoundDoc(doc.name, kind);
  const next: SoundDoc = { ...doc, kind };
  if (kind === 'sfx' && !doc.layers?.length) next.layers = fresh.layers;
  if (kind === 'song') {
    next.tempo ??= fresh.tempo;
    next.timeSignature ??= fresh.timeSignature;
    next.key ??= fresh.key;
    if (!doc.tracks?.length) {
      next.instruments = { ...(fresh.instruments ?? {}), ...(doc.instruments ?? {}) };
      next.patterns = { ...(fresh.patterns ?? {}), ...(doc.patterns ?? {}) };
      next.tracks = fresh.tracks;
    }
  }
  if (kind === 'instrument' && !doc.instrument) {
    next.instrument = fresh.instrument;
    if (!doc.patterns) next.patterns = fresh.patterns;
    next.tempo ??= fresh.tempo;
  }
  if (kind === 'voice' && !doc.voice) next.voice = fresh.voice;
  return next;
}

// Mute / solo

/**
 * The document the renderer should play for the editor's solo state: when
 * anything is soloed, every other layer/track is muted. `solo` holds
 * `layer:<index>` / `track:<index>` / `voice` keys. Returns the same doc when nothing is soloed.
 */
export function applySolo(doc: SoundDoc, solo: ReadonlySet<string> | readonly string[]): SoundDoc {
  const set = new Set(solo);
  if (set.size === 0) return doc;
  return {
    ...doc,
    layers: doc.layers?.map((l, i) => (set.has(`layer:${i}`) ? l : { ...l, mute: true })),
    tracks: doc.tracks?.map((t, i) => (set.has(`track:${i}`) ? t : { ...t, mute: true })),
    // The voice part is soloed with the `voice` key; otherwise it is silenced.
    ...(doc.voice && !set.has('voice') ? { voice: { ...doc.voice, lines: [], notes: [] } } : {}),
  };
}

function dropUndefined<T extends object>(value: T): T {
  const out = { ...value } as Record<string, unknown>;
  for (const k of Object.keys(out)) if (out[k] === undefined) delete out[k];
  return out as T;
}

/* ------------------------------------------------------------------ */
/* Referenced files                                                    */

const FILE_RE = /\.(wav|flac|ogg|mp3|aif|aiff|sf2|sf3|sound\.json)$/i;

/** Project files a document references (samples, IRs, wavetables, SoundFonts, instrument refs). */
export function referencedFiles(doc: unknown): string[] {
  const out = new Set<string>();
  const walk = (v: unknown, key: string | null) => {
    if (typeof v === 'string') {
      if (key !== 'export' && !v.startsWith('$') && FILE_RE.test(v)) out.add(v.replace(/\\/g, '/').replace(/^\.?\/+/, ''));
      return;
    }
    if (Array.isArray(v)) v.forEach((x) => walk(x, key));
    else if (v && typeof v === 'object')
      for (const [k, x] of Object.entries(v as Record<string, unknown>)) if (k !== 'export') walk(x, k);
  };
  walk(doc, null);
  return [...out];
}

/** True when the document (or an instrument in it) uses the General MIDI SoundFont. */
export function usesSoundFont(doc: unknown): boolean {
  return JSON.stringify(doc).includes('"soundfont"');
}

/** Piper voice ids the document speaks with (empty when it has no speech). */
export function usedVoices(doc: SoundDoc): string[] {
  const out = new Set<string>();
  if (doc.voice && doc.voice.mode !== 'sing') out.add(doc.voice.voice ?? 'en_US-amy-medium');
  const walk = (v: unknown) => {
    if (Array.isArray(v)) v.forEach(walk);
    else if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (o.type === 'speech' && typeof o.voice === 'string') out.add(o.voice);
      Object.values(o).forEach(walk);
    }
  };
  walk(doc.layers);
  walk(doc.tracks);
  return [...out];
}
